import { useContext, useMemo, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { compact, partition, chain } from "lodash"
import { FilterList } from "@mui/icons-material"
import SearchableMenu from "components/searchable-menu"
import { useIsOpPro } from "resources/account"
import { CONTENT_SET_METADATA } from "resources/content-sets"

// TODO: Replace onViewSelected with the proper Analytics context
export default function FactorsViewSelector({ context, contentSets, onViewSelected }) {
  const { view, setView, filter, selectedMode } = useContext(context)
  const isOpPro = useIsOpPro()

  const getLabel = useCallback((itemLabel) => {
    return filter ? <>
      <span style={{ marginRight: "0.5rem" }}><FilterList /></span>
      <span>{itemLabel}</span>
    </> : itemLabel
  }, [filter])

  const isRebalanced = selectedMode === "rebalanced"
  const [proprietaryContentSets, licensedContentSets] = useMemo(() => partition(contentSets, "proprietary"), [contentSets])
  const defaultMenuItems = useMemo(() => compact([
    {
      id: "performance",
      label: "Performance",
      groupId: "riskModel",
      groupLabel: "Risk Model",
      disabled: isRebalanced,
      tooltip: isRebalanced ? "Factor performance is not available for rebalanced position sets." : undefined,
    }, !isOpPro && {
      id: "risk",
      label: "Risk",
      groupId: "riskModel",
      groupLabel: "Risk Model",
    }, {
      id: "beta",
      label: "Beta",
      groupId: "riskModel",
      groupLabel: "Risk Model",
    },
  ]), [isOpPro, isRebalanced])

  const contentSetMenuItems = useMemo(() => ([
    ...chain(licensedContentSets)
      .sortBy("label")
      .map((contentSet) => ({
        id: contentSet.id,
        label: contentSet.name,
        groupId: "licensedContentSets",
        groupLabel: "Licensed",
      }))
      .value(),
    ...chain(proprietaryContentSets)
      .sortBy("label")
      .map((contentSet) => ({
        id: contentSet.id,
        label: contentSet.name,
        groupId: "proprietaryContentSets",
        groupLabel: "Proprietary",
      }))
      .value(),
  ]), [licensedContentSets, proprietaryContentSets])

  const menuItems = useMemo(() => [...defaultMenuItems, ...contentSetMenuItems].map((item, index) => ({ ...item, order: index + 1 })), [defaultMenuItems, contentSetMenuItems])

  useEffect(() => {
    if (menuItems.some(item => item.id === view && item.disabled)) {
      setView(menuItems.find(item => !item.disabled).id)
    }
  }, [menuItems, view, setView])

  const handleViewSelect = useCallback((selectedItem) => {
    if (selectedItem.onSelect) {
      selectedItem.onSelect()
    }

    onViewSelected?.(selectedItem.id)

    setView(selectedItem.id)
  }, [onViewSelected, setView])

  return <SearchableMenu
    items={menuItems}
    current={view}
    onSelect={handleViewSelect}
    getLabel={getLabel}
  />
}

FactorsViewSelector.propTypes = {
  context: PropTypes.object.isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  onViewSelected: PropTypes.func,
}
